/**
 * Public Training page — NOT behind Cognito.
 *
 * Agents arrive via the opaque-token link from their invite email:
 *   /training/<uuid-token>
 *
 * Flow:
 *  1. Load: GET /api/public/training/info?token=… → { email, lang, passed }
 *     - If 404 → "link not found / revoked".
 *     - If `passed=true` → show "already completed on DD/MM/YYYY".
 *  2. Walk through 2 scenarios (reactified from the standalone HTML).
 *  3. On 2/2 correct → POST /api/public/training/complete?token=…
 *     → state syncs to portal owner's team table.
 *
 * Self-contained: no portal chrome (Sidebar / Cognito), no localStorage —
 * the server is the source of truth so cross-device/cross-browser works.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Phone, CheckCircle2, X, Loader2, Crown, Eye, Send, Building2, Mail as MailIcon, ExternalLink } from 'lucide-react';
import { useLang, type Lang } from '../lib/i18n';
import { cn } from '../lib/utils';

const PUBLIC_API = (axios.defaults.baseURL ?? '') as string;

// ── types from the training i18n bundle ─────────────────────────────────────
type Tr = ReturnType<typeof useLang>['t']['training'];
type Scenario = Tr['scenarios'][number];

// Constants shared across both scenarios (the simulated end-customer).
const REQUESTER = 'Bill Lates';
const INITIALS  = 'B';
const ACCOUNT   = 'Bill Lates';
const CONTACT_EMAIL = 'bill.lates@acmecorp.com';
const CONTACT_PHONE = '32488773355';

// Per-scenario expected decision + result payload.
const SCENARIO_META = [
  {
    expected: 'approve' as const,
    result: {
      failed: false,
      doc:    'ID_CARD',
      name:   'BILL LATES',
      birth:  'SEATTLE',
      check:  'Selfie matches document',
      country:'US',
      ip:     '73.158.64.***',
      vid:    '8f6aca4e-5f45-11f1-bba4-3a7c026ef77f',
    },
  },
  {
    expected: 'reject' as const,
    result: { failed: true },
  },
];

interface InfoResponse {
  email:     string;
  lang:      Lang;
  passed:    boolean;
  passed_at: string | null;
}

// ─── Top-level Training page ────────────────────────────────────────────────
export function Training() {
  const { token } = useParams<{ token: string }>();
  const { t, lang, setLang } = useLang();
  const tr = t.training;

  const [info,   setInfo]   = useState<InfoResponse | null>(null);
  const [error,  setError]  = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [step,   setStep]   = useState<0 | 1 | 2 | 3>(0);
  const [results, setResults] = useState<[boolean | null, boolean | null]>([null, null]);

  // Load training metadata; honour the language the inviter chose by
  // syncing the portal-wide lang to it on first arrival.
  useEffect(() => {
    if (!token) { setError(tr.notFound); setLoading(false); return; }
    axios.get<InfoResponse>(`${PUBLIC_API}/api/public/training/info`, { params: { token } })
      .then(res => {
        setInfo(res.data);
        if (res.data.lang && res.data.lang !== lang) setLang(res.data.lang);
        if (res.data.passed) setStep(3);
      })
      .catch(e => {
        setError(e?.response?.status === 404 ? tr.notFound : tr.loading);
      })
      .finally(() => setLoading(false));
  }, [token]);

  // On 2/2 pass, persist server-side. We fire-and-forget so the UI advances
  // immediately; if the network is slow the user still sees the certificate.
  const markComplete = useCallback(() => {
    if (!token) return;
    axios.post(`${PUBLIC_API}/api/public/training/complete`, null, { params: { token } })
      .catch(() => { /* best-effort; failure surfaces in Sentry via interceptor */ });
  }, [token]);

  const onJudged = (idx: 0 | 1, correct: boolean) => {
    const next: [boolean | null, boolean | null] = [...results] as any;
    next[idx] = correct;
    setResults(next);
  };

  const advance = () => {
    if (step === 1) setStep(2);
    else if (step === 2) { markComplete(); setStep(3); }
  };

  // ── render ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <PublicFrame label={tr.subheader}>
        <div className="flex items-center justify-center py-20 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {tr.loading}
        </div>
      </PublicFrame>
    );
  }

  if (error || !info) {
    return (
      <PublicFrame label={tr.subheader}>
        <div className="max-w-md mx-auto bg-white border border-gray-200 rounded-xl p-8 text-center mt-12">
          <X className="h-8 w-8 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900">{tr.notFound}</h2>
        </div>
      </PublicFrame>
    );
  }

  return (
    <PublicFrame label={info.email}>
      <div className="max-w-5xl mx-auto px-5 pt-7 pb-20">

        {/* Step pills */}
        <div className="flex flex-wrap gap-2 mb-7">
          {tr.steps.map((label, i) => (
            <StepPill key={i} index={i} current={step} label={label} />
          ))}
        </div>

        {step === 0 && <WelcomePanel tr={tr} onStart={() => setStep(1)} />}
        {step === 1 && (
          <ScenarioPanel
            tr={tr}
            scenario={tr.scenarios[0]}
            meta={SCENARIO_META[0]}
            scenarioNumber={1}
            onJudged={(c) => onJudged(0, c)}
            onAdvance={advance}
          />
        )}
        {step === 2 && (
          <ScenarioPanel
            tr={tr}
            scenario={tr.scenarios[1]}
            meta={SCENARIO_META[1]}
            scenarioNumber={2}
            onJudged={(c) => onJudged(1, c)}
            onAdvance={advance}
          />
        )}
        {step === 3 && <PassedPanel tr={tr} info={info} />}
      </div>
    </PublicFrame>
  );
}

// ─── Public frame (top bar + body) ──────────────────────────────────────────
function PublicFrame({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#F6F7F9]">
      <div className="bg-[#0E1626] text-white px-7 py-3.5 flex items-center gap-3">
        <ShieldCheck className="h-7 w-7 text-[#7FA3FF]" />
        <span className="text-base font-bold tracking-tight">XyloTrust Training</span>
        <span className="ml-auto text-xs text-[#9DA9C2] font-mono">{label}</span>
      </div>
      {children}
    </div>
  );
}

// ─── Step pill ──────────────────────────────────────────────────────────────
function StepPill({ index, current, label }: { index: number; current: number; label: string }) {
  const done = index < current;
  const active = index === current;
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-xs font-semibold',
        done   && 'bg-green-50 border-green-200 text-green-700',
        active && 'bg-brand-50 border-brand-500 text-brand-700',
        !done && !active && 'bg-white border-gray-200 text-gray-400',
      )}
    >
      <span
        className={cn(
          'inline-grid place-items-center h-5 w-5 rounded-full text-[10px] font-bold',
          done   && 'bg-green-600 text-white',
          active && 'bg-brand-600 text-white',
          !done && !active && 'bg-gray-100 text-gray-400',
        )}
      >
        {done ? '✓' : index === 3 ? '✓' : index + 1}
      </span>
      {label}
    </div>
  );
}

// ─── Welcome panel ──────────────────────────────────────────────────────────
function WelcomePanel({ tr, onStart }: { tr: Tr; onStart: () => void }) {
  const features = tr.welcome.steps;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-8">
      <h1 className="text-2xl font-bold tracking-tight mb-2">{tr.welcome.title}</h1>
      <p className="text-gray-500 text-base mb-6 leading-relaxed">{tr.welcome.lead}</p>
      <h2 className="text-lg font-bold mt-5 mb-3">{tr.welcome.howItWorks}</h2>
      {features.map((f, i) => (
        <div key={i} className={cn('flex gap-3.5 py-3.5', i > 0 && 'border-t border-gray-100')}>
          <span className="h-9 w-9 flex-none rounded-lg bg-brand-50 text-brand-600 grid place-items-center">
            {i === 0 ? <Phone className="h-4 w-4" /> : i === 1 ? <ShieldCheck className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
          </span>
          <div>
            <h3 className="text-sm font-semibold">{f.title}</h3>
            <p className="text-sm text-gray-500 mt-0.5 leading-relaxed" dangerouslySetInnerHTML={{ __html: f.body }} />
          </div>
        </div>
      ))}
      <button
        onClick={onStart}
        className="mt-6 inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-lg px-6 py-3 text-sm"
      >
        {tr.welcome.cta}
      </button>
    </div>
  );
}

// ─── Scenario panel ─────────────────────────────────────────────────────────
type ScenarioMeta = typeof SCENARIO_META[number];
type WidgetStage = 'idle' | 'sending' | 'sent' | 'done';

function ScenarioPanel({
  tr, scenario, meta, scenarioNumber, onJudged, onAdvance,
}: {
  tr: Tr;
  scenario: Scenario;
  meta: ScenarioMeta;
  scenarioNumber: 1 | 2;
  onJudged: (correct: boolean) => void;
  onAdvance: () => void;
}) {
  const [stage,    setStage]    = useState<WidgetStage>('idle');
  const [phone,    setPhone]    = useState('');
  const [judged,   setJudged]   = useState<null | { correct: boolean; choice: 'approve' | 'reject' }>(null);
  const [resetKey, setResetKey] = useState(0); // bump to retry

  const phoneReady = phone.replace(/\D/g, '').length >= 6;

  const send = () => {
    if (!phoneReady) return;
    setStage('sending');
    setTimeout(() => setStage('sent'),  900);   // "message sent"
    setTimeout(() => setStage('done'), 2400);   // verification result back
  };

  const judge = (choice: 'approve' | 'reject') => {
    const correct = choice === meta.expected;
    onJudged(correct);
    setJudged({ correct, choice });
  };

  const retry = () => {
    setStage('idle'); setPhone(''); setJudged(null); setResetKey(k => k + 1);
  };

  const ticketRow = (
    <div className="bg-white border border-gray-200 rounded-xl p-7 flex flex-col">
      <div className="flex items-center gap-3 mb-1.5">
        <Phone className="h-5 w-5 text-gray-500" />
        <h2 className="text-base font-bold">{scenario.subject}</h2>
      </div>
      <p className="text-xs text-gray-500 mb-5">{tr.ui.ticketCreatedBy}</p>
      <div className="flex gap-3">
        <span className="h-8 w-8 rounded-full bg-purple-100 text-purple-700 grid place-items-center text-sm font-bold flex-none">{INITIALS}</span>
        <div className="flex-1">
          <div className="text-sm mb-2">
            <strong className="text-brand-600">{REQUESTER}</strong>{' '}
            <span className="text-gray-500">{scenario.via}</span>
          </div>
          {scenario.body.map((b, i) => (
            <p key={i} className="text-sm text-gray-700 leading-relaxed mb-2">{b}</p>
          ))}
        </div>
      </div>

      {/* Result note appears when verification is done */}
      {stage === 'done' && <ResultNote tr={tr} meta={meta} />}

      {/* Reply box mock */}
      <div className="mt-5 border border-blue-200 rounded-lg overflow-hidden">
        <div className="flex gap-1.5 px-3 pt-2.5">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-brand-50 text-brand-600 text-xs font-semibold">
            <MailIcon className="h-3 w-3" /> {tr.ui.replyTabs.reply}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-gray-400 text-xs font-semibold">
            {tr.ui.replyTabs.note}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-gray-400 text-xs font-semibold">
            {tr.ui.replyTabs.forward}
          </span>
        </div>
        <div className="flex items-center px-3.5 py-3 gap-2">
          <span className="flex-1 text-sm text-gray-400">{tr.ui.replyPlaceholder}</span>
          <span className="h-8 w-8 rounded-lg bg-brand-50 text-brand-600 grid place-items-center">
            <Send className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
    </div>
  );

  const contactCard = (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 text-sm font-semibold">
        <span className="text-gray-400"><svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span>
        {tr.ui.contactHeader}
        <span className="ml-auto text-xs text-brand-600 font-semibold cursor-pointer">{tr.ui.contactEdit}</span>
      </div>
      <div className="px-4 py-3.5">
        <div className="flex gap-2.5 mb-3">
          <span className="h-7 w-7 rounded-full bg-purple-100 text-purple-700 grid place-items-center text-xs font-bold flex-none">{INITIALS}</span>
          <div>
            <div className="text-sm font-bold text-brand-600">{ACCOUNT}</div>
            <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
              <Building2 className="h-3 w-3" /> {tr.ui.contactOrg}
            </div>
          </div>
        </div>
        <div className="mb-2.5">
          <div className="text-[11px] text-gray-500">{tr.ui.contactEmailLabel}</div>
          <div className="text-xs text-gray-700">{CONTACT_EMAIL}</div>
        </div>
        <div className="mb-2.5">
          <div className="text-[11px] text-gray-500">{tr.ui.contactPhoneLabel}</div>
          <div className="text-xs text-gray-700">{CONTACT_PHONE}</div>
        </div>
        <a href="#" onClick={e => e.preventDefault()} className="text-xs text-brand-600 inline-flex items-center gap-1.5 mt-1">
          <ExternalLink className="h-3 w-3" /> {tr.ui.contactMore}
        </a>
      </div>
    </div>
  );

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-7 mb-4">
        <span className="inline-flex items-center gap-1.5 font-mono uppercase tracking-wider text-[11px] font-semibold text-brand-600 bg-brand-50 px-2.5 py-1 rounded-full mb-3.5">
          <CheckCircle2 className="h-3 w-3" /> {tr.ui.scenarioTag(scenarioNumber)}
        </span>
        <h1 className="text-2xl font-bold tracking-tight">{tr.ui.evaluate}</h1>
        <p className="text-gray-500 text-base mt-1.5 leading-relaxed">{tr.ui.evaluateLead}</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_290px] gap-4 items-start" key={resetKey}>
        <div className="flex flex-col gap-4">
          {ticketRow}

          {/* Decision bar appears once verification result is back */}
          {stage === 'done' && !judged && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-base font-bold">{tr.ui.decideQ}</p>
              <p className="text-sm text-gray-500 mt-1 mb-4" dangerouslySetInnerHTML={{ __html: tr.ui.decideHint.replace('{account}', ACCOUNT) }} />
              <div className="flex gap-2.5 flex-wrap">
                <button
                  onClick={() => judge('approve')}
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg px-5 py-2.5 text-sm"
                >
                  <CheckCircle2 className="h-4 w-4" /> {tr.ui.approve}
                </button>
                <button
                  onClick={() => judge('reject')}
                  className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg px-5 py-2.5 text-sm"
                >
                  <X className="h-4 w-4" /> {tr.ui.reject}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3.5">
          {contactCard}
          <WidgetCard
            tr={tr}
            stage={stage}
            phone={phone}
            onPhoneChange={setPhone}
            onSend={send}
            phoneReady={phoneReady}
          />
        </div>
      </div>

      {judged && (
        <DecisionModal
          tr={tr}
          correct={judged.correct}
          explanation={judged.correct ? scenario.explainOk : scenario.explainBad}
          scenarioNumber={scenarioNumber}
          onNext={() => onAdvance()}
          onRetry={retry}
        />
      )}
    </>
  );
}

// ─── XyloTrust widget card (right column) ───────────────────────────────────
function WidgetCard({
  tr, stage, phone, onPhoneChange, onSend, phoneReady,
}: {
  tr: Tr;
  stage: WidgetStage;
  phone: string;
  onPhoneChange: (v: string) => void;
  onSend: () => void;
  phoneReady: boolean;
}) {
  const time = useMemo(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }, [stage]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden sticky top-4">
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100">
        <ShieldCheck className="h-5 w-5 text-brand-600" />
        <span className="text-sm font-bold">XyloTrust</span>
        <span className="ml-auto text-gray-300">▾</span>
      </div>

      <div className="px-4 py-4">
        {stage === 'idle' && (
          <>
            <p className="text-sm font-bold">{tr.ui.widgetTitle}</p>
            <p className="text-sm font-bold mt-0.5 mb-4">{ACCOUNT}</p>
            <p className="text-xs font-semibold text-gray-700 mb-2">{tr.ui.widgetLabel}</p>
            <div className="flex gap-2 mb-3.5">
              <span className="inline-flex items-center gap-1.5 border border-gray-200 rounded-md px-2 text-xs font-semibold text-gray-700 flex-none">
                🇧🇪 +32 ▾
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={phone}
                onChange={e => onPhoneChange(e.target.value)}
                placeholder="499887766"
                className="flex-1 min-w-0 border border-gray-200 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={onSend}
              disabled={!phoneReady}
              className={cn(
                'w-full rounded-lg px-3 py-2.5 text-sm font-bold text-left transition-colors',
                phoneReady
                  ? 'bg-brand-600 text-white hover:bg-brand-700 cursor-pointer'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed',
              )}
            >
              {tr.ui.widgetSendBtn}
            </button>
            <div className="mt-3.5 pt-3 border-t border-gray-100 text-xs">
              <a href="#" onClick={e => e.preventDefault()} className="text-brand-600 inline-flex items-center gap-1.5">
                <ExternalLink className="h-3 w-3" /> {tr.ui.widgetDirectCheck}
              </a>
            </div>
          </>
        )}

        {(stage === 'sending' || stage === 'sent') && (
          <>
            <ProgressStep state="done"   title={tr.ui.progressSent} sub={`+32 ${phone}  ${time}`} />
            <ProgressStep state="active" title={tr.ui.progressWait} sub={tr.ui.progressWaitSub} />
            <ProgressStep state="pending" title={tr.ui.progressDone} last />
          </>
        )}

        {stage === 'done' && (
          <>
            <ProgressStep state="done" title={tr.ui.progressSent} />
            <ProgressStep state="done" title={tr.ui.progressWait} />
            <ProgressStep state="done" title={tr.ui.progressDone} sub={time} crown last />
            <div className="mt-3.5 pt-3 border-t border-gray-100 text-xs">
              <a href="#" onClick={e => e.preventDefault()} className="text-brand-600 inline-flex items-center gap-1.5">
                <Eye className="h-3 w-3" /> {tr.ui.showResults}
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Single progress step inside the widget ─────────────────────────────────
function ProgressStep({
  state, title, sub, crown, last,
}: {
  state: 'done' | 'active' | 'pending';
  title: string;
  sub?: string;
  crown?: boolean;
  last?: boolean;
}) {
  return (
    <div className="flex gap-3 items-start py-2 relative">
      <span className={cn(
        'h-5 w-5 flex-none rounded-full border-2 bg-white grid place-items-center z-10',
        state === 'done'   && 'bg-green-600 border-green-600 text-white',
        state === 'active' && 'border-brand-500 text-brand-500',
        state === 'pending' && 'border-gray-200 text-gray-300',
      )}>
        {state === 'done'   && (crown
          ? <Crown className="h-3 w-3" />
          : <CheckCircle2 className="h-3 w-3" strokeWidth={3} />)}
        {state === 'active' && <Loader2 className="h-3 w-3 animate-spin" />}
        {state === 'pending' && <span className="h-2 w-2 rounded-full" />}
      </span>
      <div className="text-sm">
        <div className={cn('font-bold', state === 'pending' && 'text-gray-400 font-semibold')}>{title}</div>
        {sub && <div className="text-xs text-gray-500">{sub}</div>}
      </div>
      {!last && (
        <span
          className={cn(
            'absolute left-[9px] top-7 bottom-0 w-[2px]',
            state === 'done' ? 'bg-green-600' : 'bg-gray-200',
          )}
        />
      )}
    </div>
  );
}

// ─── Result note (yellow private note in ticket) ────────────────────────────
function ResultNote({ tr, meta }: { tr: Tr; meta: ScenarioMeta }) {
  if (meta.result.failed) {
    return (
      <div className="bg-yellow-50 rounded-lg px-4 py-3.5 mt-4 text-sm leading-relaxed">
        <div className="text-xs text-gray-600 mb-2">
          <strong className="text-brand-600">{tr.ui.notePrefix.split(' ').slice(0, 2).join(' ')}</strong>{' '}
          {tr.ui.notePrefix.split(' ').slice(2).join(' ')}
        </div>
        <div><b>{tr.ui.noteFailedTitle}</b></div>
        <div>{tr.ui.noteFailedBody}</div>
      </div>
    );
  }
  const r = meta.result;
  return (
    <div className="bg-yellow-50 rounded-lg px-4 py-3.5 mt-4 text-sm leading-relaxed font-mono">
      <div className="text-xs text-gray-600 mb-2 font-sans">
        <strong className="text-brand-600">{tr.ui.notePrefix.split(' ').slice(0, 2).join(' ')}</strong>{' '}
        {tr.ui.notePrefix.split(' ').slice(2).join(' ')}
      </div>
      <div><b>{tr.ui.noteOkTitle}</b></div>
      <div className="h-2" />
      <div><span className="text-gray-500">{tr.ui.noteDoc}</span> {r.doc}</div>
      <div className="h-2" />
      <div><span className="text-gray-500">{tr.ui.noteName}</span> <b>{r.name}</b></div>
      <div><span className="text-gray-500">{tr.ui.noteBirth}</span> {r.birth}</div>
      <div><span className="text-gray-500">{tr.ui.noteCheck}</span> {r.check}</div>
      <div className="h-2" />
      <div><span className="text-gray-500">{tr.ui.noteCountry}</span> {r.country}</div>
      <div><span className="text-gray-500">{tr.ui.noteIP}</span> {r.ip}</div>
      <div><span className="text-gray-500">{tr.ui.noteVid}</span> {r.vid}</div>
    </div>
  );
}

// ─── Decision modal (correct / not correct popup) ───────────────────────────
function DecisionModal({
  tr, correct, explanation, scenarioNumber, onNext, onRetry,
}: {
  tr: Tr;
  correct: boolean;
  explanation: string;
  scenarioNumber: 1 | 2;
  onNext: () => void;
  onRetry: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/55 backdrop-blur-sm p-5">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
        <div className={cn(
          'h-16 w-16 rounded-full grid place-items-center mx-auto mb-4',
          correct ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600',
        )}>
          {correct
            ? <CheckCircle2 className="h-8 w-8" strokeWidth={2.4} />
            : <X className="h-8 w-8" strokeWidth={2.4} />}
        </div>
        <h3 className={cn('text-xl font-bold mb-2', correct ? 'text-green-700' : 'text-red-700')}>
          {correct ? tr.ui.feedbackOk : tr.ui.feedbackBad}
        </h3>
        <p className="text-sm text-gray-700 leading-relaxed mb-6">{explanation}</p>
        {correct ? (
          <button
            onClick={onNext}
            className="bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-lg px-6 py-3 text-sm min-w-[200px]"
          >
            {scenarioNumber === 1 ? tr.ui.nextBtn : tr.ui.finishBtn}
          </button>
        ) : (
          <button
            onClick={onRetry}
            className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-semibold rounded-lg px-6 py-3 text-sm min-w-[200px]"
          >
            {tr.ui.retryBtn}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Passed panel (certificate) ─────────────────────────────────────────────
function PassedPanel({ tr, info }: { tr: Tr; info: InfoResponse }) {
  const { lang } = useLang();
  const datelocale = lang === 'nl' ? 'nl-BE' : 'en-GB';
  const isoDate = info.passed_at ?? new Date().toISOString();
  const human = new Date(isoDate).toLocaleDateString(datelocale, { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="bg-white border border-gray-200 rounded-xl px-7 py-12 text-center">
      <div className="h-20 w-20 rounded-full bg-green-50 text-green-600 grid place-items-center mx-auto mb-4">
        <ShieldCheck className="h-10 w-10" strokeWidth={2.2} />
      </div>
      <h1 className="text-2xl font-bold mb-2">{tr.passed.title}</h1>
      <p className="text-gray-500 max-w-prose mx-auto mb-6 leading-relaxed">{tr.passed.body}</p>
      <div className="inline-flex flex-col gap-1 border border-dashed border-gray-200 rounded-xl px-7 py-4 mb-6">
        <span className="text-base font-bold">{info.email}</span>
        <span className="text-xs font-mono text-gray-500">
          {tr.passed.certName} · {info.passed ? `${tr.passed.already} ${human}` : `${tr.passed.certMeta} ${human}`}
        </span>
      </div>
    </div>
  );
}
